import React, { useEffect, useState } from 'react';
import { SearchUI } from 'src/components/ui/Search';
import FilterOptions from './FilterOptions';
import { DateFilter, DateRangeFilter, FilterSection } from './FilterSection';
import {
  DateFilterValue,
  FilterConfig,
  FilterForPlanActionProps,
  FilterKey,
  FilterKeysWithOptions,
  FiltersConfig,
  FiltersState,
  LoadingStates,
  SelectionFilterValue,
  UserListItem,
} from '../../../types/typesVPO';
import { FlexCol, FlexRow } from 'src/utils/flexLayouts';
import Loading from 'src/components/Loading';
import { useAppDispatch } from 'src/hooks/redux';
import { searchPeople } from 'src/components/ui/form/peoplePicker/actions';
import { UserSearch } from './UserSearch';
import { isEqual } from 'lodash';

interface ResetFilterConfig {
  [filterName: string]: string[];
}

const FilterForPlanAction = ({ onFiltersChange, filterConfigs, filtersMap, initialFilters, filtersOpen, filtersConfig, responsibleFil, selectedGropu, heightFilter, selectAllKeys }: FilterForPlanActionProps) => {
  const [filters, setFilters] = useState<FiltersState>(initialFilters);
  const [openSections, setOpenSections] = useState<Record<FilterKey, boolean>>(filtersOpen);
  const [searchString, setSearchString] = useState('');

  const dispatch = useAppDispatch();

  const [loadingStates, setLoadingStates] = useState<LoadingStates>(() =>
    Object.keys(filtersConfig).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {}
    )
  );

  useEffect(() => {
    if (searchString.length >= 3) {
      dispatch(searchPeople(searchString));
    }
  }, [searchString, dispatch]);

  useEffect(() => {
    const hasData = Object.values(filtersMap).some((arr: any) => arr && arr.length);
    if (!hasData) return;

    const updatedConfig = { ...filtersConfig };
    let needsUpdate = false;

    (Object.entries(filtersMap) as [FilterKeysWithOptions, Array<{ Title: string }> | undefined][]).forEach(([key, value]) => {
      const newOptions = value?.map((item) => item.Title) || [];
      if (!isEqual(updatedConfig[key].options, newOptions)) {
        updatedConfig[key].options = newOptions;
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setLoadingStates((prev) => {
        const newState = { ...prev };
        Object.keys(filtersMap).forEach((key) => {
          newState[key] = false;
        });
        return newState;
      });
    }
  }, [filtersMap, filtersConfig]);

  const toggleSection = (section: string) => {
    setOpenSections((prev: any) => ({ ...prev, [section]: !prev[section] }));
  };

  const resetSectionFilters = (filterType: FilterKey) => {
    setFilters((prev: any) => {
      const newFilters = {
        ...prev,
        [filterType]: {
          ...initialFilters[filterType],
        },
      };
      onFiltersChange?.(newFilters);
      return newFilters;
    });
  };

  const isSectionFilterApplied = (filterType: FilterKey) => {
    const filter = filters[filterType];

    if ('selected' in filter) {
      return Array.isArray((filter as SelectionFilterValue).selected) && (filter as SelectionFilterValue).selected.length > 0;
    }

    if ('startDate' in filter) {
      const dateFilter = filter as DateFilterValue;
      return Boolean(dateFilter.startDate) || Boolean(dateFilter.endDate);
    }

    return false;
  };

  //! для сброса фильтров относительно выбранного другого
  //! например column - та секция, которую меняем и сбрасываются все, кроме тех, что указаны в массиве
  const resetFilterConfig: ResetFilterConfig = {
    column: ['yearFactory', 'monthFactory', 'status'],
    columnPlanAction: ['yearFactory', 'monthFactory', 'status'],
    groupPlanAction: ['yearFactory', 'monthFactory', 'column', 'status'],
    groupPlan: ['yearFactory', 'monthFactory', 'column', 'columnPlanAction', 'status'],

    columnReportAction: ['year'],

    group: ['year'],
  };

  const toggleFilter = (filterType: string, value: string, nameSection: string, resetConfig: ResetFilterConfig) => {
    //! выбор только одного значения
    if (
      filterType === 'year' ||
      filterType === 'month' ||
      (filterType === 'group' && selectedGropu) ||
      filterType === 'groupPlanAction' ||
      filterType === 'groupPlan' ||
      filterType === 'columnPlanAction' ||
      filterType === 'formGop' ||
      filterType === 'columnReportAction' ||
      filterType === 'locationKPI'||
      filterType === 'kpiName'||
      filterType === 'location5S'||
      filterType === 'department5S'
    ) {
      setFilters((prev: any) => {
        const preserveFilters = resetConfig[nameSection];

        if (preserveFilters) {
          const newFilters: any = {};

          Object.keys(prev).forEach((key) => {
            const hasDateFields = prev[key].hasOwnProperty('startDate') && prev[key].hasOwnProperty('endDate');

            if (preserveFilters.includes(key) || key === nameSection) {
              if (key === nameSection) {
                const selectedItem = filterConfigs[key]?.items ? filterConfigs[key].items.find((i: any) => i.Title === value) : null;
                const itemId = selectedItem ? (selectedItem.id !== undefined ? selectedItem.id : selectedItem.ID) : undefined;

                newFilters[key] = {
                  selected: [value],
                  selectedIds: itemId !== undefined ? [itemId] : [],
                  search: '',
                };
              } else {
                newFilters[key] = prev[key];
              }
            } else {
              newFilters[key] = hasDateFields ? { startDate: '', endDate: '' } : { selected: [], selectedIds: [], search: '' };
            }
          });

          onFiltersChange?.(newFilters);
          return newFilters;
        }

        const selectedItem = filterConfigs[filterType]?.items ? filterConfigs[filterType].items.find((i: any) => i.Title === value) : null;
        const itemId = selectedItem ? (selectedItem.id !== undefined ? selectedItem.id : selectedItem.ID) : undefined;

        const newFilters = {
          ...prev,
          [filterType]: {
            ...prev[filterType],
            selected: [value],
            selectedIds: itemId !== undefined ? [itemId] : [],
          },
        };
        onFiltersChange?.(newFilters);
        return newFilters;
      });
      return;
    }

    if (!filterConfigs[filterType]) {
      setFilters((prev: any) => {
        const newFilters = {
          ...prev,
          [filterType]: {
            ...prev[filterType],
            selected: prev[filterType].selected.includes(value) ? prev[filterType].selected.filter((item: any) => item !== value) : [...prev[filterType].selected, value],
          },
        };
        onFiltersChange?.(newFilters);
        return newFilters;
      });
      return;
    }

    const item = filterConfigs[filterType].items.find((i: any) => i.Title === value);
    if (!item) return;

    setFilters((prev: any) => {
      const selected = prev[filterType].selected.includes(value) ? prev[filterType].selected.filter((title: string) => title !== value) : [...prev[filterType].selected, value];

      const selectedIds = selected
        .map((title: string) => {
          const item = filterConfigs[filterType].items.find((i: any) => i.Title === title);
          return item ? (item.id !== undefined ? item.id : item.ID) : undefined;
        })
        .filter((id: any) => id !== undefined);

      const newFilters = {
        ...prev,
        [filterType]: {
          ...prev[filterType],
          selected,
          selectedIds,
        },
      };
      onFiltersChange?.(newFilters);
      return newFilters;
    });
  };

  const isAllSelected = (filterType: FilterKey) => {
    const items = filterConfigs[filterType]?.items || [];
    const selected = (filters[filterType] as SelectionFilterValue)?.selected || [];
    return items.length > 0 && selected.length >= items.length;
  };

  //! Выбрать все / Снять все
  const toggleSelectAll = (filterType: FilterKey) => {
    const items = filterConfigs[filterType]?.items || [];
    const allSelected = isAllSelected(filterType);
    setFilters((prev: any) => {
      const newFilters = {
        ...prev,
        [filterType]: allSelected
          ? { ...initialFilters[filterType] }
          : {
              ...prev[filterType],
              selected: items.map((i: any) => i.Title),
              selectedIds: items
                .map((i: any) => (i.id !== undefined ? i.id : i.ID))
                .filter((id: any) => id !== undefined),
            },
      };
      onFiltersChange?.(newFilters);
      return newFilters;
    });
  };

  const handleSearch = (filterType: FilterKey, value: string) => {
    if (filterType === 'responsible') {
      setSearchString(value);
    }

    setFilters((prev: any) => ({
      ...prev,
      [filterType]: { ...prev[filterType], search: value },
    }));
  };

const handleDateRangeChange = (filterType: FilterKey, value: { startDate: string | null; endDate: string | null }) => {
  setFilters((prev: any) => {
    const newFilters = {
      ...prev,
      [filterType]: value,
    };
    onFiltersChange?.(newFilters);
    return newFilters;
  });
};

  const resetAllFilters = () => {
    setFilters(initialFilters);
    onFiltersChange?.(initialFilters);
  };

  const isAnyFilterApplied = () => {
    return Object.values(filters).some((filter) => {
      if ('selected' in filter && filter.selected) return filter.selected.length > 0;
      if ('startDate' in filter) return filter.startDate || filter.endDate;
      return false;
    });
  };

  const handleUserFilterChange = (filterKey: FilterKey, users: UserListItem[]) => {
    setFilters((prev: any) => {
      const newFilters = {
        ...prev,
        [filterKey]: {
          ...prev[filterKey],
          selected: users.map((user) => user.email),
          selectedIds: users.map((user) => user.id),
          items: users,
        },
      };

      onFiltersChange?.(newFilters);
      return newFilters;
    });
  };

  const renderFilterContent = (key: FilterKey, config: FilterConfig) => {
    if (key === 'created' || key === 'dataTable' || key === 'dataOfPrescription') {
      const filter = filters[key] as DateFilterValue;
     return (
      <DateRangeFilter
        value={filter}
        onChange={(value) => handleDateRangeChange(key, value)}
        startLabel={key === 'created' ? '' :
                   key === 'dataTable' ? '' : ''}
        monthYearOnly={config.monthYearOnly || false}
      />
      );
    }

    if ((key === 'responsible' && responsibleFil) || config.userSearch) {
      const selectedUsers = ((filters[key] as SelectionFilterValue).items as UserListItem[]) || [];
      return (
        <UserSearch
          selectedUsers={selectedUsers}
          onUserFilterChange={(users) => handleUserFilterChange(key, users)}
          initialSearchValue={(filters[key] as SelectionFilterValue).search || ''}
        />
      );
    }

    return (
      <div className='px-5 pb-5'>
        {config.hasSearch !== false && (
          <SearchUI placeholder={config.searchPlaceholder || 'Поиск'} defaultValue={(filters[key] as SelectionFilterValue).search} onClick={(value) => handleSearch(key, value)} />
        )}
        {selectAllKeys?.includes(key) && (config.options?.length ?? 0) > 0 && (
          <div className='mt-3 text-sm text-[#012169] cursor-pointer hover:underline w-fit' onClick={() => toggleSelectAll(key)}>
            {isAllSelected(key) ? 'Снять все' : 'Выбрать все'}
          </div>
        )}
        <FilterOptions
          options={config.options || []}
          selectedOptions={(filters[key] as SelectionFilterValue).selected}
          toggleOption={(value) => toggleFilter(key, value, key, resetFilterConfig)}
          searchValue={(filters[key] as SelectionFilterValue).search}
          filterType={key}
        />
      </div>
    );
  };

  return (
    <div className='w-full bg-white rounded-lg overflow-hidden'>
      <div className={`overflow-y-auto scrollbar scrollbar-thumb-rounded-lg scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] ${heightFilter}`}>
        {isAnyFilterApplied() && (
          <FlexRow className='items-center p-5 border-b cursor-pointer group gap-x-2 flex-nowrap' onClick={resetAllFilters}>
            <FlexCol className='group-hover:underline text-sm text-[#637381]'>Сбросить все фильтры</FlexCol>
            <FlexCol>
              <svg width='12' height='12' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <g clipPath='url(#clip0_381_6476)'>
                  <path
                    d='M7.9999 7.19998L1.8999 1.09998C1.6749 0.874975 1.3249 0.874975 1.0999 1.09998C0.874902 1.32498 0.874902 1.67497 1.0999 1.89997L7.1999 7.99998L1.0999 14.1C0.874902 14.325 0.874902 14.675 1.0999 14.9C1.3249 15.125 1.6749 15.125 1.8999 14.9L7.9999 8.79998L14.0999 14.9C14.3249 15.125 14.6749 15.125 14.8999 14.9C14.9999 14.8 15.0749 14.65 15.0749 14.5C15.0749 14.35 15.0249 14.2 14.8999 14.1L8.7999 7.99998L14.8999 1.89997C14.9999 1.79997 15.0749 1.64997 15.0749 1.49998C15.0749 1.34998 15.0249 1.19998 14.8999 1.09998C14.6749 0.874975 14.3249 0.874975 14.0999 1.09998L7.9999 7.19998Z'
                    fill='#637381'
                  />
                </g>
                <defs>
                  <clipPath id='clip0_381_6476'>
                    <rect width='16' height='16' fill='white' transform='translate(0 16) rotate(-90)' />
                  </clipPath>
                </defs>
              </svg>
            </FlexCol>
          </FlexRow>
        )}

        {Object.entries(filtersConfig as FiltersConfig).map(([key, config]) => {
          if (key === 'responsible' && !responsibleFil) {
            return null;
          }
          const filterDependencies = {
            groupPlanAction: {
              dependsOn: 'column',
              message: 'Для отображения данных выберите значение в секции "Колонна"',
            },
            groupPlan: {
              dependsOn: 'columnPlanAction',
              message: 'Для отображения данных выберите значение в секции "Колонна"',
            },
            factory: {
              dependsOn: 'columnPlanAction',
              message: 'Для отображения данных выберите значение в секции "Колонна"',
            },
            subdivision: {
              dependsOn: 'columnPlanAction',
              message: 'Для отображения данных выберите значение в секции "Колонна"',
            },
            factoryReport: {
              dependsOn: 'groupReportPeriod',
              message: 'Сначала выберите значение в секции "Группа"',
            },
            columnBlock: {
              dependsOn: 'factory',
              message: 'Сначала выберите значение в секции "Завод"',
            },
            demand: {
              dependsOn: 'columnBlock',
              message: 'Сначала выберите значение в секции "Блок колонна"',
            },
            blockDemand: {
              dependsOn: 'direction',
              message: 'Выберите значение в секции "Направление"',
            },
             block: {
              dependsOn: 'direction',
              message: 'Выберите значение в секции "Направление"',
            },
             demandGop: {
              dependsOn: 'block',
              message: 'Выберите значение в секции "Блок"',
            },
          };
          const filterKey = key as FilterKey;
          const selectedCount = (filters[filterKey] as SelectionFilterValue).selected?.length || 0;

          const dependency = filterDependencies[key];
          const isDisabled = dependency ? !filters[dependency.dependsOn]?.selectedIds?.length : false;
          const disabledMessage = dependency?.message || '';

          return (
            <FilterSection
              key={filterKey}
              title={`${config.title}${selectedCount > 0 ? ` · ${selectedCount}` : ''}`}
              isOpen={openSections[filterKey]}
              toggleOpen={() => toggleSection(filterKey)}
              onReset={() => resetSectionFilters(filterKey)}
              showReset={isSectionFilterApplied(filterKey)}
              disabled={isDisabled}
              disabledMessage={disabledMessage}
            >
              {renderFilterContent(filterKey, config)}
            </FilterSection>
          );
        })}
      </div>
    </div>
  );
};

export default FilterForPlanAction;
